// eslint-disable-next-line max-classes-per-file
import { Injectable } from '@angular/core';
import {
    IDataflowFormatTypeMetadata,
    IDataflowTypeMetadata,
    IDataflowTypeMetadataSink,
    IDataflowTypeMetadataSource
} from 'app/extensibility/metadatas/resource-type/dataflow-format-type-metadata';
import { DataflowFormatRegistryService } from './dataflow-format-registry.service';
import { LinkedServiceRegistryService } from '../linked-service/linked-service-registry.service';
import { ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';
import {
    DataFlowDatasetSettings,
    DatasetFormatType
} from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { DataFlowDSLAdapterRegistry, IViolation, Metamodel, Owned } from 'shell-models-lib';
import { BaseFormatAdapter } from 'app/model/adapters/dataflow/formatAdapters/base-format.adapter';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { DataFlowSink } from 'app/model/dataflow/dataflow-sink';
import { DataFlowExpression } from 'app/model/dataflow/dataflowexpression';
import { DataFlowExpressionUtils } from 'app/model/adapters/dataflow/expression.util';
import { IConnectorProperty } from 'app/services/dynamic-connector.service.models';
import { mapDatatypeToDataflowType } from 'app/services/datatype-to-expressiontype';
import { sanitizeAndFormatString, stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { BehaviorSubject } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class DataflowFormatDynamicLoaderService {
    public isInitialized$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    constructor(
        private readonly _dataflowFormatRegistry: DataflowFormatRegistryService,
        private readonly _linkedServiceRegistryService: LinkedServiceRegistryService,
    ) {}

    /**
     * must call this after linkedServiceDynamicLoader.initialize()
     */
    public initialize(): void {
        if (this.isInitialized$.value) {
            return;
        }
        this.loadAllInlineMetadataFromLinkedService();
        this.isInitialized$.next(true);
    }

    public loadAllInlineMetadataFromLinkedService(): void {
        this._dataflowFormatRegistry.clear();
        this._loadAllInlineMetadata(this._linkedServiceRegistryService.getAllLinkedServiceConfigurations());
    }

    private _loadAllInlineMetadata(metadata: ILinkedServiceTypeMetadata[]): void {
        const inlineTypeMap: Map<string, IDataflowFormatTypeMetadata> = new Map<string, IDataflowFormatTypeMetadata>();
        metadata.forEach(({ dataflowSource, dataflowSink, ...linkedServiceMetadata }) => {
            if (dataflowSource === undefined && dataflowSink === undefined) {
                return;
            }
            const {
                type: linkedServiceType,
                displayName: linkedServiceDisplayName,
                icon: linkedServiceIcon,
                linkedServiceKind,
                category = []
            } = linkedServiceMetadata;
            const mapDataflow = (dataflowMetadata: IDataflowTypeMetadata[], isSource: boolean): void => {
                if (Array.isArray(dataflowMetadata)) {
                    dataflowMetadata.forEach((dataflow: IDataflowTypeMetadata) => {
                        if (dataflow.disabled) {
                            return;
                        }
                        const {
                            type,
                            controls,
                            icon,
                            settings,
                            displayName,
                            notShowInTypeSelection,
                            properties,
                            format,
                            mdfFabric
                        } = dataflow;
                        if (!inlineTypeMap.has(type)) {
                            inlineTypeMap.set(dataflow.type, {
                                type,
                                icon: icon ?? linkedServiceIcon,
                                displayName: displayName ?? linkedServiceDisplayName,
                                notShowInTypeSelection,
                                isCDCOnly: linkedServiceMetadata.isCDCOnly,
                                settings,
                                get supportSource(): boolean {
                                    return this.linkedServiceSourceTypes.length > 0;
                                },
                                get supportSink(): boolean {
                                    return this.linkedServiceSinkTypes.length > 0;
                                },
                                get isDynamicallyGenerated(): boolean {
                                    return this.sourceProperties?.length > 0 || this.sinkProperties?.length > 0;
                                },
                                linkedServiceSourceTypes: [],
                                linkedServiceSinkTypes: [],
                                sourceControls: isSource ? controls : undefined,
                                sinkControls: isSource ? undefined : controls,
                                sourceProperties: isSource ? properties : undefined,
                                sinkProperties: isSource ? undefined : properties,
                                format,
                                category,
                                mdfFabric
                            });
                        }
                        const metadata = inlineTypeMap.get(type);
                        if (metadata.format === undefined) {
                            metadata.format = format;
                        }
                        if (isSource) {
                            if (!metadata.linkedServiceSourceTypes.includes(linkedServiceType)) {
                                metadata.linkedServiceSourceTypes.push(linkedServiceType);
                            }
                            if (metadata.sourceControls === undefined) {
                                metadata.sourceControls = controls;
                            }
                            if (metadata.sourceProperties === undefined) {
                                metadata.sourceProperties = properties;
                            }
                            metadata.useInlineProjection = (
                                dataflow as IDataflowTypeMetadataSource
                            ).useInlineProjection;
                            metadata.dataflowKind =
                                (dataflow as IDataflowTypeMetadataSource).dataflowKind ?? linkedServiceKind;
                            metadata.connectTransformationProperties = (
                                dataflow as IDataflowTypeMetadataSource
                            ).connectTransformationProperties;
                        } else {
                            if (!metadata.linkedServiceSinkTypes.includes(linkedServiceType)) {
                                metadata.linkedServiceSinkTypes.push(linkedServiceType);
                            }
                            if (metadata.sinkControls === undefined) {
                                metadata.sinkControls = controls;
                            }
                            if (metadata.sinkProperties === undefined) {
                                metadata.sinkProperties = properties;
                            }
                            metadata.enableImportSchemaInSink = Boolean(
                                (dataflow as IDataflowTypeMetadataSink).enableImportSchemaInSink
                            );
                            metadata.enableAlterRow = Boolean((dataflow as IDataflowTypeMetadataSink).enableAlterRow);
                        }
                    });
                }
            };
            mapDataflow(dataflowSource, true);
            mapDataflow(dataflowSink, false);
        });
        const metadataList = Array.from(inlineTypeMap.values());
        metadataList.forEach((metadata) => {
            if (metadata.isDynamicallyGenerated) {
                const properties = (metadata.sourceProperties ?? []).reduce((previousValue, currentValue) => {
                    if (!previousValue.map((p) => p.propertyName).includes(currentValue.propertyName)) {
                        previousValue.push(currentValue);
                    }
                    return previousValue;
                }, Array.from(metadata.sinkProperties ?? []));

                @Metamodel.model({
                    name: metadata.settings,
                    validator: Settings.validateSettings
                })
                class Settings extends DataFlowDatasetSettings implements Record<string, unknown> {
                    public static validateSettings(settings: Settings): IViolation[] {
                        const violations: IViolation[] = [];
                        properties.forEach((property) => {
                            const { isRequired, propertyName, displayName } = property;
                            if (
                                isRequired &&
                                stringIsNullOrEmpty((settings[propertyName] as DataFlowExpression)?.expression)
                            ) {
                                violations.push({
                                    message: () =>
                                        sanitizeAndFormatString(stringResources.RequiredTemplate, displayName)
                                });
                            }
                        });
                        return violations;
                    }

                    [propName: string]: unknown;

                    public getDatasetType(): string {
                        return metadata.datasetType;
                    }

                    public getFormat(): DatasetFormatType {
                        return metadata.type as DatasetFormatType;
                    }

                    public getIconClass(): string {
                        return metadata.icon.className?.startsWith('svg-')
                            ? metadata.icon.className.slice(4)
                            : metadata.icon.className;
                    }

                    public getSubFormat(): string {
                        return metadata.subFormat;
                    }

                    public initialize(subFormat: string): void {
                        for (const property of properties) {
                            const { propertyName, dataType, defaultValue } = property;
                            this[propertyName] = this.transform.createTemplatableExpression(
                                mapDatatypeToDataflowType(dataType),
                                defaultValue != null ? String(defaultValue) : undefined
                            );
                        }
                    }
                }
                properties.forEach((property) => {
                    Metamodel.attribute({
                        display: property.displayName
                    })(Settings.prototype, property.propertyName);
                });

                @DataFlowDSLAdapterRegistry.adapter({
                    model: Metamodel.modelFor(Settings)
                })
                class Adapter extends BaseFormatAdapter<Settings> {
                    public async toSourceDSL(setting: Settings): Promise<string[]> {
                        const props: string[] = [];
                        props.push(`store: '${metadata.type}'`);
                        if (metadata.format) {
                            props.push(`format: '${metadata.format}'`);
                        }
                        props.push(...this._propertiesToDSL(setting, metadata.sourceProperties));
                        return props;
                    }

                    public async toSourceModel(
                        owner: DataFlowSource,
                        configs: Map<string, string>,
                        handledConfigs: string[]
                    ): Promise<Settings> {
                        const setting = this._generateSetting(owner);
                        this._propertiesToModel(setting, owner, configs, handledConfigs, metadata.sourceProperties);
                        return setting;
                    }

                    public async toSinkDSL(setting: Settings): Promise<string[]> {
                        const props: string[] = [];
                        props.push(`store: '${metadata.type}'`);
                        if (metadata.format) {
                            props.push(`format: '${metadata.format}'`);
                        }
                        props.push(...this._propertiesToDSL(setting, metadata.sinkProperties));
                        return props;
                    }

                    public async toSinkModel(
                        owner: DataFlowSink,
                        configs: Map<string, string>,
                        handledConfigs: string[]
                    ): Promise<Settings> {
                        const setting = this._generateSetting(owner);
                        this._propertiesToModel(setting, owner, configs, handledConfigs, metadata.sinkProperties);
                        return setting;
                    }

                    protected _generateSetting(owner: DataFlowSource | DataFlowSink): Settings {
                        const setting = Owned.create<Settings>(Settings.MODEL, owner);
                        setting.initialize(metadata.subFormat);
                        return setting;
                    }

                    private _propertiesToDSL(setting: Settings, properties: IConnectorProperty[]): string[] {
                        const props: string[] = [];
                        for (const property of properties) {
                            const { propertyName, dataType, resourceName = propertyName } = property;
                            const attribute = setting[propertyName];
                            if (attribute instanceof DataFlowExpression && attribute.expression) {
                                props.push(
                                    `${resourceName}: ${DataFlowExpressionUtils.escapeNewlineInDslString(
                                        DataFlowExpressionUtils.wrapExpressionConfigValue(
                                            attribute,
                                            mapDatatypeToDataflowType(dataType)
                                        )
                                    )}`
                                );
                            }
                        }
                        return props;
                    }

                    private _propertiesToModel(
                        setting: Settings,
                        owner: DataFlowSource | DataFlowSink,
                        configs: Map<string, string>,
                        handledConfigs: string[],
                        properties: IConnectorProperty[]
                    ): void {
                        for (const property of properties) {
                            const { propertyName, dataType, resourceName = propertyName } = property;
                            if (configs.has(resourceName)) {
                                setting[propertyName] = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                                    configs.get(resourceName),
                                    owner,
                                    mapDatatypeToDataflowType(dataType)
                                );
                                handledConfigs.push(resourceName);
                            }
                        }
                    }
                }
                import('app/model/adapters/adapter-loader').then((m) => {
                    m.AdapterLoader.adapters.push(Adapter);
                });
            }
        });
        this._dataflowFormatRegistry.registerInlineConfig(...metadataList);
    }
}
